import { useCallback } from 'react';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import {
  selectedGamesRandomWhere,
  selectedGamesWhatObj,
  selectedGameWhere
} from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import RidGenerator from 'utils/swarm/rid-generator';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand } from 'services/get-data-level';

type Params = {
  setGameData: Function;
  orderStartTime: boolean;
  limit: number;
};

export const useGetSelectedGames = ({
  setGameData,
  orderStartTime,
  limit
}: Params): ((gameIds: number[], fetchRandom?: boolean) => void) => {
  const sortedData = useCallback(
    (swarmData: { sport: Record<string, Sport> }): any => {
      let data = [];

      if (
        swarmData?.sport &&
        Object.values(swarmData.sport).length &&
        Object.values(swarmData.sport)[0]
      ) {
        for (const sport in swarmData.sport) {
          const regions = swarmData.sport[sport].region;

          for (const region in regions) {
            const competitions = regions[region].competition;

            for (const competition in competitions) {
              const games = competitions[competition].game;

              for (const game in games) {
                games[game].sportId = Number(sport);
                games[game].sportName = swarmData.sport[sport].alias;
                games[game].sportType = swarmData.sport[sport].type;
                games[game].regionName = regions[region].name;
                games[game].regionAlias = regions[region].alias;
                games[game].regionId = regions[region].id;
                games[game].competitionName = competitions[competition].name;
                games[game].competitionId = competitions[competition].id;
                data.push(games[game]);
              }
            }
          }
        }
      }

      if (data.length) {
        if (orderStartTime) {
          data = sortByOrder(Object.values(data), 'start_ts');
        }

        if (limit && data.length > limit) {
          data.length = limit;
        }
      }

      return data.length ? data : [];
    },
    [limit]
  );

  const setData = useCallback(
    (swarmData: { sport: Record<string, Sport> } | null) => {
      setGameData(swarmData ? sortedData(swarmData) : null);
    },
    [setGameData, sortedData]
  );

  const updateData = useCallback(
    (updatedSwarmData: { sport: Record<string, Sport> }) => {
      setGameData(sortedData(updatedSwarmData));
    },
    [setGameData, sortedData]
  );

  return useCallback(
    (gameIds: number[], fetchRandom = false) => {
      if (gameIds.length === 0 && !fetchRandom && !SpringConfigs.MOCKED_DATA) {
        setData(null);
      } else {
        const rid = `SELECTED_SPORT${RidGenerator.gForSubscribe()}`;
        executeCommand(
          rid,
          selectedGamesWhatObj,
          !fetchRandom && !SpringConfigs.MOCKED_DATA
            ? selectedGameWhere(gameIds)
            : selectedGamesRandomWhere(),
          setData,
          true,
          updateData
        );
      }
    },
    [setData, updateData]
  );
};
